import { useId } from 'react';
import { useTranslation } from '@mukil/i18n';
import {
  Avatar,
  Button,
  GlassCard,
  SegmentedControl,
} from '@jumpstone/ui-react';
import { ScreenHeader } from '../../components/common/screen_header';
import { useEssNotificationsControl } from '../../components/layout/ess_notifications';
import { useSignOutAction } from '../../components/layout/use_sign_out_action';
import {
  LOCALE_NAME_KEYS,
  active_wagepro_locale,
} from '../../components/layout/locale_options';
import { useSession } from '../../data/use_session';
import { WAGEPRO_LOCALES } from '../../lib/i18n';

/**
 * The employee (ESS) Profile tab: who is signed in, and the account-level
 * settings an employee has — language, notifications, sign out.
 *
 * Nothing here is new behaviour; it gathers what the app bar already offers
 * into one thumb-reachable screen, as the phone design does:
 * - Language writes through the same `setLocale` (and the same choice list,
 *   `locale_options.ts`) as `EmployeeLocaleSwitcher`.
 * - Notifications opens the SAME bell panel (`ess_notifications.tsx`), so the
 *   list and unread count cannot differ between the two ways in. Hidden when
 *   there is no panel to open (outside the employee surface).
 * - Sign out is `useSignOutAction`, the one sign-out action the account
 *   menus' `SignOutMenuItem` also renders from.
 */
export function MyProfileScreen(): React.JSX.Element {
  const { t, locale, setLocale } = useTranslation('wp');
  const { session } = useSession();
  const sign_out = useSignOutAction();
  const notifications = useEssNotificationsControl();
  const language_label_id = useId();

  const language_options = WAGEPRO_LOCALES.map((value) => ({
    value,
    label: t(LOCALE_NAME_KEYS[value]),
  }));

  return (
    <div className="max-w-[720px] mx-auto">
      <ScreenHeader title={t('my_profile.title')} />

      <GlassCard className="rounded-[24px] p-[18px] mb-4 flex items-center gap-3.5">
        <Avatar name={session.name} colorVariant="teal" size="xl" />
        <div className="min-w-0">
          <p className="text-[18px] font-extrabold tracking-[-0.01em] break-words">
            {session.name}
          </p>
          <p className="mt-0.5 text-[13px] text-[color:var(--ink-muted)] break-words">
            {session.company_name}
          </p>
        </div>
      </GlassCard>

      <GlassCard className="rounded-[24px] overflow-hidden">
        <div className="flex flex-col gap-2.5 px-4 py-3.5">
          <span id={language_label_id} className="text-[15px]">
            {t('my_profile.language')}
          </span>
          <SegmentedControl
            fill
            aria-labelledby={language_label_id}
            options={language_options}
            value={active_wagepro_locale(locale)}
            onValueChange={(value) => {
              setLocale(value);
            }}
          />
        </div>
        {notifications && (
          <Button
            variant="ghost"
            className="w-full min-h-14 px-4 justify-between rounded-none border-t border-[var(--line)] text-[15px] font-normal"
            onClick={(event) => {
              notifications.open_from(event.currentTarget);
            }}
          >
            {t('notification.heading')}
            <ChevronRightIcon />
          </Button>
        )}
        <Button
          variant="ghost-danger"
          className="w-full min-h-14 px-4 justify-start rounded-none border-t border-[var(--line)] text-[15px] font-semibold"
          onClick={sign_out.sign_out}
        >
          {sign_out.label}
        </Button>
      </GlassCard>
    </div>
  );
}

function ChevronRightIcon(): React.JSX.Element {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="text-[color:var(--ink-muted)] rtl:-scale-x-100"
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}
